import type { AxiosError } from 'axios';
import axios from 'axios';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AppCode, type Org } from '../../../enum';
import { APIStatus } from '../../../enum';
import { getCSV, removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { dateTimeFilterFormat } from '../program-dashboard/program-dashboard.helper';
import type { DashboardBody, DashboardParams } from '../program-dashboard/programDashboardSlice';

const { logError } = RaygunErrorHandlerService();

export type PeerEvaluation = {
  total_count: number;
  query_start_date: string;
  query_end_date: string;
  query_org_name: string | null;
  id: string;
  app_code: AppCode;
  org_code: Org;
  org_name: string;
  org_timezone_name: string;
  created: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  score: number;
  total_hours: number | null;
  total_expected_hours: number | null;
  percent_weeks_80plus: number;
  total_chats: number;
  missed_chats: number;
  chat_length_minutes: number | null;
  surveys_complete: number | null;
  percent_survey_completion: number | null;
  thumbs_up: number | null;
  thumbs_down: number | null;
  percent_thumbs_up: number | null;
  percent_thumbs_down: number | null;
  total_thumbs: number;
  positive_feedback: number | null;
  negative_feedback: number | null;
  percent_positive_feedback: number | null;
  percent_negative_feedback: number | null;
  total_rated_feedback: number | null;
  months_of_service: number;
};

type PeerEvalParams = DashboardParams & RequestParams<PeerEvaluation>;

export type PeerEvalBody = DashboardBody & RequestParams<PeerEvaluation>;

type AdminPeerEvalSliceType = {
  peerEvalParams: PeerEvalParams;
  peerEvals: PeerEvaluation[];
  csv: string;
  adminPeerEvalApiStatus: APIStatus;
  adminPeerEvalCSVApiStatus: APIStatus;
};

const initialState: AdminPeerEvalSliceType = {
  peerEvalParams: {
    app_code: AppCode.TalkingStick,
    start_date: dayjs().subtract(1, 'month'),
    end_date: dayjs(),
    selected_org_code: 'allOrgs',
    for_leps: true,
    for_service_providers: true,
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<PeerEvaluation>('score', 'ascend'),
    filteredInfo: {},
  },
  peerEvals: [],
  csv: '',
  adminPeerEvalApiStatus: APIStatus.IDLE,
  adminPeerEvalCSVApiStatus: APIStatus.IDLE,
};

export const fetchPeerEvaluations = createAsyncThunk<PeerEvaluation[], undefined, AsyncThunkConfig>(
  'adminPeerEval/fetchPeerEvaluations',
  async (_, thunkAPI) => {
    try {
      const peerEvalParams = thunkAPI.getState().adminPeerEvalSlice.peerEvalParams;
      const body: PeerEvalBody = {
        ...peerEvalParams,
        start_date: peerEvalParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: peerEvalParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: peerEvalParams.selected_org_code === 'allOrgs' ? null : peerEvalParams.selected_org_code,
      };

      const response = (await axios.post('v0_get_leps_evaluation', body)) as PeerEvaluation[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminPeerEvalSlice', 'fetchPeerEvaluations']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const exportPeerEvaluations = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'adminPeerEval/exportPeerEvaluations',
  async (_, thunkAPI) => {
    try {
      const peerEvalParams = thunkAPI.getState().adminPeerEvalSlice.peerEvalParams;
      const body: PeerEvalBody = {
        ...peerEvalParams,
        start_date: peerEvalParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: peerEvalParams.end_date.endOf('day').format(dateTimeFilterFormat),
        selected_org_code: peerEvalParams.selected_org_code === 'allOrgs' ? null : peerEvalParams.selected_org_code,
        pagination: {},
        sortedInfo: new SortedInfo<PeerEvaluation>('score', 'ascend'),
        filteredInfo: {},
      };

      const response = (await axios.post('v0_get_leps_evaluation', body)) as PeerEvaluation[];
      const headers =
        'total_count,query_start_date,query_end_date,query_org_name,id,app_code,org_code,org_name,org_timezone_name,created,first_name,last_name,full_name,email,score,total_hours,total_expected_hours,percent_weeks_80plus,total_chats,missed_chats,chat_length_minutes,surveys_complete,percent_survey_completion,percent_thumbs_up,percent_thumbs_down,total_thumbs,percent_positive_feedback,percent_negative_feedback,total_rated_feedback,months_of_service';
      return getCSV(response ?? [], headers.split(','));
    } catch (e) {
      logError(e, ['adminPeerEvalSlice', 'exportPeerEvaluations']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminPeerEvalSlice = createSlice({
  name: 'adminPeerEval',
  initialState,
  reducers: {
    handleParamsChange: (state, action: PayloadAction<Partial<DashboardParams>>) => {
      state.peerEvalParams = { ...state.peerEvalParams, ...action.payload };
    },
    handlePeerEvalTableChange: (state, action: PayloadAction<TableChange<PeerEvaluation>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<PeerEvaluation>;
      state.peerEvalParams.pagination = action.payload.pagination;
      state.peerEvalParams.sortedInfo = sorter;
      state.peerEvalParams.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.peerEvalParams.pagination = initialState.peerEvalParams.pagination;
      state.peerEvalParams.sortedInfo = initialState.peerEvalParams.sortedInfo;
      state.peerEvalParams.filteredInfo = initialState.peerEvalParams.filteredInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPeerEvaluations.pending, (state, _action) => {
        state.adminPeerEvalApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchPeerEvaluations.fulfilled, (state, action) => {
        state.peerEvals = action.payload;
        state.adminPeerEvalApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchPeerEvaluations.rejected, (state, _action) => {
        state.adminPeerEvalApiStatus = APIStatus.ERROR;
      })
      .addCase(exportPeerEvaluations.pending, (state, _action) => {
        state.adminPeerEvalCSVApiStatus = APIStatus.PENDING;
      })
      .addCase(exportPeerEvaluations.fulfilled, (state, action) => {
        state.csv = action.payload;
        state.adminPeerEvalCSVApiStatus = APIStatus.FULFILLED;
      })
      .addCase(exportPeerEvaluations.rejected, (state, _action) => {
        state.adminPeerEvalCSVApiStatus = APIStatus.ERROR;
      });
  },
});

export const { handleParamsChange, handlePeerEvalTableChange, clearFilter } = adminPeerEvalSlice.actions;
